"use client"

import { router } from "@inertiajs/react"
import { Eye, EyeOff } from "@untitledui/icons"

import { CollabStatusBadge } from "@/components/admin/collabs/status-badge"
import { ButtonUtility } from "@/components/ui/button-utility"
import { Table, TableCard } from "@/components/ui/table"
import { adminCollabPath, collabPath } from "@/routes"
import { BriefStatus } from "@/types/contents"
import type { Brief } from "@/types/serializers"

export type CollabRow = Brief["brief"]

interface CollabsTableProps {
  rows: CollabRow[]
  "aria-label"?: string
  footer?: React.ReactNode
}

/**
 * Table of collabs for the admin collabs index page. Clicking a row
 * navigates to that collab page.
 */
export function CollabsTable({
  rows,
  "aria-label": ariaLabel = "Collabs",
  footer,
}: CollabsTableProps) {
  return (
    <TableCard.Root>
      <Table aria-label={ariaLabel} className="bg-primary">
        <Table.Header>
          <Table.Head
            id="title"
            isRowHeader
            label="Collab"
            className="w-full"
          />
          <Table.Head id="slug" label="Slug" className="w-auto" />
          <Table.Head id="status" label="Status" />
          <Table.Head
            id="suggestions"
            label="Suggestions"
            className="text-right"
          />
          <Table.Head id="actions" label="" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: CollabRow) => (
            <Table.Row
              id={row.id}
              className="cursor-pointer"
              onAction={() => router.visit(adminCollabPath(row.slug))}
            >
              <Table.Cell>
                <p className="text-primary text-sm font-medium whitespace-nowrap">
                  {row.title}
                </p>
              </Table.Cell>
              <Table.Cell>
                <p className="text-primary text-sm font-medium whitespace-nowrap">
                  {row.slug}
                </p>
              </Table.Cell>
              <Table.Cell>
                <CollabStatusBadge status={row.status} />
              </Table.Cell>
              <Table.Cell className="text-tertiary text-right tabular-nums">
                {row.suggestions_count}
              </Table.Cell>
              <Table.Cell>
                <div className="flex justify-end gap-0.5">
                  {row.status === (BriefStatus.published as string) ? (
                    <ButtonUtility
                      size="sm"
                      color="tertiary"
                      icon={Eye}
                      tooltip="View"
                      href={collabPath(row.slug)}
                    />
                  ) : (
                    <ButtonUtility
                      size="sm"
                      color="tertiary"
                      icon={EyeOff}
                      tooltip="Restricted"
                      disabled
                    />
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {footer}
    </TableCard.Root>
  )
}
